import React, { useEffect, useRef, useState } from 'react';
import IndividualModal from './components/IndividualModal';
import { DuplicateCandidate, PendingSave } from './components/DuplicateSuggestions';

interface Individual {
  id: string;
  first_name?: string;
  middle_names?: string;
  surname?: string;
  is_placeholder?: boolean;
  gender?: string;
  dob?: string;
  status?: string;
  death_dob?: string;
  metadata?: { [key: string]: any };
  is_progenitor?: boolean;
  progenitor_line_name?: string;
  is_ghost?: boolean;
  shared_dna_cm?: number;
  longest_segment_cm?: number;
  number_of_segments?: number;
  percent_shared_dna?: number;
  predicted_relationship?: string;
  actual_relationship?: string;
}

interface Relationship {
  id: string;
  from_id: string;
  to_id: string;
  type: string;
  sources?: string[];
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const PAGE_SIZE = 30;
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const IndividualsPage: React.FC = () => {
  const [individuals, setIndividuals] = useState<Individual[]>([]);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [modalIndividual, setModalIndividual] = useState<any>({});
  // Potential parent/child summaries
  const [potentialParentsMap, setPotentialParentsMap] = useState<{[id: string]: any[]}>({});
  const [potentialChildrenMap, setPotentialChildrenMap] = useState<{[id: string]: any[]}>({});
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [duplicateCandidates, setDuplicateCandidates] = useState<DuplicateCandidate[]>([]);
  // Last add-form submission, replayed by "create anyway"
  const pendingSaveRef = useRef<PendingSave | null>(null);
  const [validationResults, setValidationResults] = useState<any>(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  // Alphabet filter + pagination
  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchIndividuals();
    fetchRelationships();
  }, []);

  useEffect(() => {
    // Fetch potential parents/children in small batches to avoid exhausting browser connection limits
    if (individuals.length === 0) return;
    let cancelled = false;
    const BATCH_SIZE = 3;

    const fetchOne = async (ind: Individual) => {
      const [parentsData, childrenData] = await Promise.all([
        fetch(`${API_URL}/individuals/${ind.id}/potential_parents`).then(r => r.json()).catch(() => []),
        fetch(`${API_URL}/individuals/${ind.id}/potential_children`).then(r => r.json()).catch(() => []),
      ]);
      if (!cancelled) {
        setPotentialParentsMap(prev => ({ ...prev, [ind.id]: parentsData }));
        setPotentialChildrenMap(prev => ({ ...prev, [ind.id]: childrenData }));
      }
    };

    const fetchBatch = async () => {
      for (let i = 0; i < individuals.length; i += BATCH_SIZE) {
        if (cancelled) break;
        const batch = individuals.slice(i, i + BATCH_SIZE);
        await Promise.all(batch.map(fetchOne));
      }
    };

    fetchBatch();
    return () => { cancelled = true; };
  }, [individuals]);

  const fetchIndividuals = () => {
    setFetching(true);
    setFetchError(null);
    fetch(`${API_URL}/individuals`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch individuals');
        return res.json();
      })
      .then((data) => {
        setIndividuals(data);
        setFetching(false);
      })
      .catch((err) => {
        setFetchError('Could not load individuals. Please check your connection or try again.');
        setFetching(false);
      });
  };

  const fetchRelationships = () => {
    setFetchError(null);
    fetch(`${API_URL}/relationships`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch relationships');
        return res.json();
      })
      .then((data) => setRelationships(data))
      .catch((err) => {
        setFetchError('Could not load relationships. Please check your connection or try again.');
      });
  };

  // Helper: Get parents for a given individual
  const getParents = (childId: string) => {
    const parentRels = relationships.filter(
      (rel: Relationship) => rel.to_id === childId && (rel.type === 'Bio Father' || rel.type === 'Bio Mother')
    );
    const bioFatherRel = parentRels.find((rel: Relationship) => rel.type === 'Bio Father');
    const bioMotherRel = parentRels.find((rel: Relationship) => rel.type === 'Bio Mother');
    const father = bioFatherRel ? individuals.find((ind: Individual) => ind.id === bioFatherRel.from_id) : null;
    const mother = bioMotherRel ? individuals.find((ind: Individual) => ind.id === bioMotherRel.from_id) : null;
    return { father, mother };
  };

  // Open modal for add
  const handleAddIndividual = () => {
    setDuplicateError(null);
    setDuplicateCandidates([]);
    setModalMode('add');
    setModalIndividual({});
    setModalOpen(true);
  };

  // Open modal for edit
  const handleEditIndividual = (ind: any) => {
    setDuplicateError(null);
    setDuplicateCandidates([]);
    setModalMode('edit');
    setModalIndividual(ind);
    setModalOpen(true);
  };

  // Save handler
  const handleSaveIndividual = async (
    ind: any,
    bioFatherId: string | null,
    bioMotherId: string | null,
    spouseId: string | null,
    adoptiveFatherId: string | null,
    adoptiveMotherId: string | null,
    allowDuplicate: boolean = false,
  ) => {
    setDuplicateError(null);
    setDuplicateCandidates([]);
    const payload = {
      ...ind,
      is_ghost: !!ind.is_ghost,
    };
    let savedId = ind.id;
    if (modalMode === 'add') {
      try {
        const res = await fetch(`${API_URL}/individuals${allowDuplicate ? '?allow_duplicate=true' : ''}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res.status === 409) {
          const data = await res.json();
          pendingSaveRef.current = [ind, bioFatherId, bioMotherId, spouseId, adoptiveFatherId, adoptiveMotherId, true];
          setDuplicateCandidates(data.candidates || []);
          return;
        }
        if (!res.ok) throw new Error('Failed to add individual');
        const data = await res.json();
        savedId = data.id;
      } catch (err) {
        setDuplicateError('Failed to add individual.');
        return;
      }
    } else {
      await fetch(`${API_URL}/individuals/${ind.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    }
    const syncParentEdge = async (relType: string, parentId: string | null) => {
      const existing = relationships.filter(r => r.to_id === savedId && r.type === relType);
      for (const r of existing) {
        await fetch(`${API_URL}/relationships/${r.id}`, { method: 'DELETE' });
      }
      if (parentId && parentId !== savedId) {
        await fetch(`${API_URL}/relationships`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ from_id: parentId, to_id: savedId, type: relType }),
        });
      }
    };
    await syncParentEdge('Bio Father', bioFatherId);
    await syncParentEdge('Bio Mother', bioMotherId);
    await syncParentEdge('Adoptive Father', adoptiveFatherId);
    await syncParentEdge('Adoptive Mother', adoptiveMotherId);
    // Create spouse relationship if needed
    if (spouseId && spouseId !== savedId) {
      // Check if already exists
      const exists = relationships.some(r => ((r.from_id === savedId && r.to_id === spouseId) || (r.from_id === spouseId && r.to_id === savedId)) && r.type === 'Spouse');
      if (!exists) {
        await fetch(`${API_URL}/relationships`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ from_id: savedId, to_id: spouseId, type: 'Spouse' }),
        });
      }
    }
    setModalOpen(false);
    fetchIndividuals();
    fetchRelationships();
  };

  const handleDeleteIndividual = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this individual? This cannot be undone.')) return;
    await fetch(`${API_URL}/individuals/${id}`, { method: 'DELETE' });
    setModalOpen(false);
    fetchIndividuals();
    fetchRelationships();
  };

  const handleRunValidation = async () => {
    setValidationLoading(true);
    setShowValidation(true);
    try {
      const res = await fetch(`${API_URL}/validate`);
      const data = await res.json();
      setValidationResults(data);
    } catch (e) {
      setValidationResults({ error: 'Failed to run validation.' });
    }
    setValidationLoading(false);
  };

  return (
    <section className="genealogy-section">
      <h1 className="heading-1">All Individuals</h1>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 18 }}>
        <button className="genealogy-btn-secondary" onClick={handleRunValidation} disabled={validationLoading}>
          {validationLoading ? 'Validating...' : 'Run Data Validation'}
        </button>
        {showValidation && (
          <button className="genealogy-btn-secondary" style={{ color: 'var(--color-error)' }} onClick={() => setShowValidation(false)}>Dismiss Validation Results</button>
        )}
      </div>
      {showValidation && validationResults && (
        <div className="genealogy-card" style={{ marginBottom: 24, background: '#fffbe6', border: '1.5px solid #ffe082', color: '#222', boxShadow: '0 2px 8px rgba(255,193,7,0.08)' }}>
          <h3 style={{ color: '#b28704', marginTop: 0, marginBottom: 12 }}>Data Validation Results</h3>
          {validationResults.error && <div style={{ color: 'var(--color-error)', fontWeight: 600 }}>{validationResults.error}</div>}
          {(validationResults.general || []).map((msg: string, i: number) => (
            <div key={i} style={{ color: '#e65100', marginBottom: 10 }}><span role="img" aria-label="Warning">⚠️</span> {msg}</div>
          ))}
          <div style={{ marginBottom: 10 }}>
            <strong>Orphan Segments:</strong>
            {validationResults.orphans && validationResults.orphans.length === 0 && <span style={{ color: '#388e3c', marginLeft: 8 }}>No orphans found.</span>}
            {validationResults.orphans && validationResults.orphans.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {validationResults.orphans.map((o: any, i: number) => (
                  <li key={i} style={{ color: '#e65100' }}><span role="img" aria-label="Warning">⚠️</span> Segment <b>{o.segment_id}</b>: {o.reason} {o.match_id && <span>(match_id: {o.match_id})</span>}</li>
                ))}
              </ul>
            )}
          </div>
          <div style={{ marginBottom: 10 }}>
            <strong>Cycles in Ancestry:</strong>
            {validationResults.cycles && validationResults.cycles.length === 0 && <span style={{ color: '#388e3c', marginLeft: 8 }}>No cycles found.</span>}
            {validationResults.cycles && validationResults.cycles.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {validationResults.cycles.map((c: any, i: number) => (
                  <li key={i} style={{ color: c.ghost ? '#b28704' : '#c62828' }}><span role="img" aria-label={c.ghost ? 'Ghost' : 'Error'}>{c.ghost ? '👻' : '⛔'}</span> {c.warning}</li>
                ))}
              </ul>
            )}
          </div>
          <div style={{ marginBottom: 10 }}>
            <strong>Duplicate DNA Segments:</strong>
            {validationResults.duplicate_segments && validationResults.duplicate_segments.length === 0 && <span style={{ color: '#388e3c', marginLeft: 8 }}>No duplicates found.</span>}
            {validationResults.duplicate_segments && validationResults.duplicate_segments.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {validationResults.duplicate_segments.map((d: any, i: number) => (
                  <li key={i} style={{ color: '#e65100' }}><span role="img" aria-label="Warning">⚠️</span> Segment <b>{d.segment_id}</b> is a duplicate of <b>{d.duplicate_of}</b></li>
                ))}
              </ul>
            )}
          </div>
          <div style={{ marginBottom: 10 }}>
            <strong>Conflicting Parents:</strong>
            {validationResults.multiple_parents && validationResults.multiple_parents.length === 0 && <span style={{ color: '#388e3c', marginLeft: 8 }}>No conflicts found.</span>}
            {validationResults.multiple_parents && validationResults.multiple_parents.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {validationResults.multiple_parents.map((m: any, i: number) => (
                  <li key={i} style={{ color: m.ghost ? '#b28704' : '#c62828' }}><span role="img" aria-label={m.ghost ? 'Ghost' : 'Error'}>{m.ghost ? '👻' : '⛔'}</span> <b>{m.child_name}</b> has {m.parent_names.length} {m.type} links: {m.parent_names.join(', ')}</li>
                ))}
              </ul>
            )}
          </div>
          <div style={{ marginBottom: 10 }}>
            <strong>Long Ghost Chains:</strong>
            {validationResults.ghost_chains && validationResults.ghost_chains.length === 0 && <span style={{ color: '#388e3c', marginLeft: 8 }}>None longer than 4.</span>}
            {validationResults.ghost_chains && validationResults.ghost_chains.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {validationResults.ghost_chains.map((g: any, i: number) => (
                  <li key={i} style={{ color: '#b28704' }}><span role="img" aria-label="Ghost">👻</span> {g.length} ghosts in a row: {g.names.join(' → ')}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      <button className="genealogy-btn-primary" onClick={handleAddIndividual} style={{ marginBottom: 24 }}>Add Individual</button>
      {fetchError && (
        <div className="individuals-page-error" style={{ color: 'var(--color-error)', marginBottom: 18, fontWeight: 600, fontSize: '1.1em' }}>{fetchError}</div>
      )}
      {fetching ? (
        <div>Loading...</div>
      ) : !fetchError && (() => {
        // Sort by surname then first name
        const sorted = [...individuals].sort((a, b) => {
          const sa = (a.surname || '').toLowerCase();
          const sb = (b.surname || '').toLowerCase();
          if (sa !== sb) return sa < sb ? -1 : 1;
          const fa = (a.first_name || '').toLowerCase();
          const fb = (b.first_name || '').toLowerCase();
          return fa < fb ? -1 : fa > fb ? 1 : 0;
        });

        // Which letters actually have individuals
        const lettersWithData = new Set(sorted.map(ind => {
          const s = (ind.surname || '').toUpperCase();
          return s && /^[A-Z]/.test(s) ? s[0] : '#';
        }));

        // Filter by active letter
        const filtered = activeLetter
          ? sorted.filter(ind => {
              if (activeLetter === '#') return !ind.surname || !/^[A-Z]/i.test(ind.surname);
              return (ind.surname || '').toUpperCase().startsWith(activeLetter);
            })
          : sorted;

        const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
        const safePage = Math.min(currentPage, totalPages);
        const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

        return (
          <>
            {/* Alphabet bar */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 16, alignItems: 'center' }}>
              <button
                className={activeLetter === null ? 'genealogy-btn-primary' : 'genealogy-btn-secondary'}
                style={{ padding: '4px 10px', fontSize: 13, minWidth: 44 }}
                onClick={() => { setActiveLetter(null); setCurrentPage(1); }}
              >All</button>
              {ALPHABET.map(letter => (
                <button
                  key={letter}
                  className={activeLetter === letter ? 'genealogy-btn-primary' : 'genealogy-btn-secondary'}
                  style={{ padding: '4px 8px', fontSize: 13, minWidth: 32, opacity: lettersWithData.has(letter) ? 1 : 0.3 }}
                  onClick={() => { setActiveLetter(letter); setCurrentPage(1); }}
                  disabled={!lettersWithData.has(letter)}
                >{letter}</button>
              ))}
              {lettersWithData.has('#') && (
                <button
                  className={activeLetter === '#' ? 'genealogy-btn-primary' : 'genealogy-btn-secondary'}
                  style={{ padding: '4px 8px', fontSize: 13, minWidth: 32 }}
                  onClick={() => { setActiveLetter('#'); setCurrentPage(1); }}
                >#</button>
              )}
            </div>

            {/* Result count */}
            <div style={{ color: '#888', fontSize: 13, marginBottom: 10 }}>
              {filtered.length} individual{filtered.length !== 1 ? 's' : ''}
              {activeLetter ? ` with surname starting '${activeLetter}'` : ' total'}
              {totalPages > 1 ? ` — page ${safePage} of ${totalPages}` : ''}
            </div>

            <table className="individuals-page-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Progenitor Line(s)</th>
                  <th>Parents</th>
                  <th>Potential Parents</th>
                  <th>Potential Children</th>
                  <th>DNA Match</th>
                  <th>Ghost</th>
                  <th>Edit</th>
                </tr>
              </thead>
              <tbody>
                {paged.map(ind => {
                  const { father, mother } = getParents(ind.id);
                  const ghostIcon = ind.is_ghost ? ' 👻' : '';
                  const hasDnaMatch = !!(
                    (ind.shared_dna_cm && Number(ind.shared_dna_cm) > 0) ||
                    (ind.longest_segment_cm && Number(ind.longest_segment_cm) > 0) ||
                    (ind.number_of_segments && Number(ind.number_of_segments) > 0) ||
                    (ind.percent_shared_dna && Number(ind.percent_shared_dna) > 0)
                  );
                  return (
                    <tr key={ind.id}>
                      <td>{[ind.first_name, ind.middle_names, ind.surname].filter(Boolean).join(' ') || 'Unknown'}{ghostIcon}</td>
                      <td>{ind.is_progenitor && ind.progenitor_line_name ? ind.progenitor_line_name : ''}</td>
                      <td>
                        {father && <span>{[father.first_name, father.middle_names, father.surname].filter(Boolean).join(' ')} (Father)</span>}
                        {father && mother && <span>, </span>}
                        {mother && <span>{[mother.first_name, mother.middle_names, mother.surname].filter(Boolean).join(' ')} (Mother)</span>}
                        {!(father || mother) && <span>-</span>}
                      </td>
                      <td>
                        {(potentialParentsMap[ind.id] || []).map((p: any, i: number) => (
                          <span key={p.individual.id} style={{ marginRight: 6 }}>
                            {[p.individual.first_name, p.individual.middle_names, p.individual.surname].filter(Boolean).join(' ') || 'Unknown'}{p.individual.is_ghost ? ' 👻' : ''}
                            {i < (potentialParentsMap[ind.id] || []).length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </td>
                      <td>
                        {(potentialChildrenMap[ind.id] || []).map((c: any, i: number) => (
                          <span key={c.individual.id} style={{ marginRight: 6 }}>
                            {[c.individual.first_name, c.individual.middle_names, c.individual.surname].filter(Boolean).join(' ') || 'Unknown'}{c.individual.is_ghost ? ' 👻' : ''}
                            {i < (potentialChildrenMap[ind.id] || []).length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {hasDnaMatch ? <span role="img" aria-label="DNA Match" title="DNA Match">🧬</span> : <span role="img" aria-label="No DNA Match" title="No DNA Match" style={{ opacity: 0.3 }}>🧬</span>}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {ind.is_ghost ? <span role="img" aria-label="Ghost" title="Ghost">👻</span> : <span role="img" aria-label="Not Ghost" title="Not Ghost" style={{ opacity: 0.3 }}>👻</span>}
                      </td>
                      <td>
                        <button className="genealogy-btn-primary" onClick={() => handleEditIndividual(ind)}>Edit</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination controls */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 20 }}>
                <button className="genealogy-btn-secondary" style={{ padding: '4px 12px' }}
                  onClick={() => setCurrentPage(1)} disabled={safePage === 1}>«</button>
                <button className="genealogy-btn-secondary" style={{ padding: '4px 12px' }}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage === 1}>‹</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
                  .reduce((acc: (number | '…')[], p, i, arr) => {
                    if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push('…');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((item, i) =>
                    item === '…'
                      ? <span key={`ellipsis-${i}`} style={{ color: '#888', padding: '0 4px' }}>…</span>
                      : <button key={item} className={safePage === item ? 'genealogy-btn-primary' : 'genealogy-btn-secondary'}
                          style={{ padding: '4px 10px', minWidth: 36 }}
                          onClick={() => setCurrentPage(item as number)}>{item}</button>
                  )}
                <button className="genealogy-btn-secondary" style={{ padding: '4px 12px' }}
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>›</button>
                <button className="genealogy-btn-secondary" style={{ padding: '4px 12px' }}
                  onClick={() => setCurrentPage(totalPages)} disabled={safePage === totalPages}>»</button>
              </div>
            )}
          </>
        );
      })()}
      <IndividualModal
        open={modalOpen}
        mode={modalMode}
        individual={modalIndividual}
        allIndividuals={individuals}
        allRelationships={relationships}
        onSave={handleSaveIndividual}
        onCancel={() => setModalOpen(false)}
        loading={false}
        duplicateError={duplicateError}
        onDelete={modalMode === 'edit' ? handleDeleteIndividual : undefined}
        duplicateCandidates={duplicateCandidates}
        onCreateAnyway={() => { if (pendingSaveRef.current) handleSaveIndividual(...pendingSaveRef.current); }}
        onOpenExisting={(id) => {
          const existing = individuals.find(i => i.id === id);
          if (existing) handleEditIndividual(existing);
        }}
        onMerged={() => {
          setModalOpen(false);
          fetchIndividuals();
          fetchRelationships();
        }}
      />
    </section>
  );
};

export default IndividualsPage; 